/**
 * Hand-authored editorial SVG — no raster, no AI generation, just vector paths.
 *
 * An envelope + a small "sparkle" badge (the AI) + a ribbon of tool-call ticks
 * escaping the top, in DESIGN.md's palette (off-black + Fin Orange on warm cream).
 *
 * If you'd rather swap in a downloaded illustration (e.g. unDraw "Mail sent",
 * Storyset "AI email", OpenDoodles "envelope"), replace this component's body
 * with the downloaded <svg>…</svg> and keep the outer props contract.
 */

export function EmailAgentIllustration({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 560 420"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Illustration: an AI agent reading and replying to an email"
      className={className}
    >
      {/* Warm cream backdrop (matches page) */}
      <rect width="560" height="420" fill="none" />

      {/* Stacked envelope behind — depth cue */}
      <g transform="translate(110 130)" opacity="0.4">
        <rect width="340" height="220" rx="8" fill="#faf9f6" stroke="#111111" strokeWidth="2" />
      </g>
      <g transform="translate(90 120)" opacity="0.65">
        <rect width="340" height="220" rx="8" fill="#faf9f6" stroke="#111111" strokeWidth="2" />
      </g>

      {/* Primary envelope */}
      <g transform="translate(70 110)">
        <rect width="340" height="220" rx="8" fill="#ffffff" stroke="#111111" strokeWidth="2" />

        {/* Flap */}
        <path d="M0 0 L170 120 L340 0" fill="none" stroke="#111111" strokeWidth="2" strokeLinejoin="round" />

        {/* Content preview lines */}
        <g transform="translate(30 150)">
          <rect width="180" height="10" rx="2" fill="#111111" />
          <rect y="20" width="240" height="10" rx="2" fill="#111111" opacity="0.5" />
          <rect y="40" width="200" height="10" rx="2" fill="#111111" opacity="0.5" />
        </g>

        {/* "Draft" stamp */}
        <g transform="translate(240 170)">
          <rect width="78" height="28" rx="4" fill="none" stroke="#ff5600" strokeWidth="2" />
          <text x="39" y="19" textAnchor="middle"
                fontFamily="'Saans', 'Inter var', 'Inter', system-ui, sans-serif"
                fontWeight="600" fontSize="12" fill="#ff5600" letterSpacing="1.5">
            DRAFT
          </text>
        </g>
      </g>

      {/* AI sparkle badge — bottom right of envelope */}
      <g transform="translate(380 290)">
        <circle cx="0" cy="0" r="36" fill="#ff5600" />
        <g stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" fill="none">
          {/* 4-point sparkle */}
          <path d="M0 -16 L0 16 M-16 0 L16 0" />
          <path d="M-10 -10 L10 10 M-10 10 L10 -10" opacity="0.65" />
        </g>
      </g>

      {/* Tool-call ticks escaping the top — shows the agent calling 3 tools */}
      <g transform="translate(230 70)">
        {[0, 1, 2].map((i) => (
          <g key={i} transform={`translate(${i * 50 - 50} ${-i * 14})`}>
            <rect x="-18" y="-10" width="36" height="20" rx="4" fill="#ffffff" stroke="#111111" strokeWidth="1.5" />
            <circle cx="0" cy="0" r="3" fill="#ff5600" />
          </g>
        ))}
        {/* Connecting line from envelope top */}
        <path d="M0 10 L-50 -14 M0 10 L0 -14 M0 10 L50 -42"
              stroke="#111111" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="3 4" fill="none" />
      </g>
    </svg>
  );
}
